/*
  BMP accessibility fixes for vertimax.com (WCAG 2.1 AA) - Oct 12, 2026
  Upload as a theme asset named a11y-fixes.js, then add this ONE line
  to layout/theme.liquid directly above </body>:

    <script src="{{ 'a11y-fixes.js' | asset_url }}" defer></script>

  Fixes:
   1. Off-screen slider slides hidden from screen readers / keyboard (1.3.1, 2.4.3, 4.1.2)
   2. Decorative slider arrow icons hidden from screen readers (1.1.1)
   3. Sticky header no longer covers the focused element (2.4.11)
   4. Trustindex review "Read more" toggles usable by keyboard (2.1.1, 4.1.2)
   5. Decorative parallax hero background image given empty alt (1.1.1)
   6. Review star ratings read as "Rated 5 out of 5 stars" (1.1.1)
   7. Repeated "Google" source icons in each review marked decorative (1.1.1)
   8. Links that open a new tab announce it to screen readers (3.2.5)
   9. Any remaining <b> in page content converted to <strong> (1.3.1)
  10. Label-style headings before the H1 converted to text so the outline starts at H1 (1.3.1, 2.4.6)
  11. Product card eyebrow labels changed to #0A6FD6 for contrast (1.4.3)
  12. PaigeChat widget wrapped in a labeled "complementary" landmark (landmark best practice)
  13. Trustindex rating summary: no nested link-in-button, no pointer cursor on non-focusable parts;
      empty "Read more" placeholders hidden (2.1.1, 4.1.2)
  14. Every iframe has a title: PayTomorrow, HubSpot "Contact form", and hidden tracking frames
      (Shopify pixel, Facebook) titled and hidden from assistive tech (2.4.1, 4.1.2)
  15. Same-destination links share one accessible name: logo "VertiMax", cart "View cart",
      V-Series/Raptor links "Explore V-Series Platforms"/"Explore Raptor Systems" (3.2.4)
  16. Contrast: review dates #595959; HubSpot form placeholders #6b6b6b and Submit button #0A6FD6 (1.4.3)
  17. Decorative empty-alt images also get role="presentation" + aria-hidden (1.1.1)
  18. Auto-playing background videos get a visible Pause/Play button, and stay paused for
      visitors who ask for reduced motion (2.2.2)
  19. Facebook pixel's hidden data form is hidden from assistive tech and labeled (1.3.1, 4.1.2)
  (1 also now removes links in hidden slides from the tab order, 4.1.2)
  (4 also now takes empty "Read more" placeholders out of the tab order, and makes only the outer
     span the button so there are no nested controls; it also reports aria-expanded)
*/
(function () {
  // CSS fixes (3, 4, 11)
  var css =
    'html{scroll-padding-top:calc(var(--header-height,91px) + 16px);}' +
    '.bb-card__eyebrow{color:#0A6FD6 !important;}' +
    '.ti-read-more-active:focus-visible,.ti-read-more:focus-visible,.ti-footer:focus-visible{outline:2px solid currentColor;outline-offset:2px;}' +
    '.ti-widget .ti-read-more:empty,.ti-widget .ti-read-more[style*="opacity: 0"],.ti-widget .ti-read-more[style*="opacity:0"]{display:none !important;}' +
    '.ti-widget .ti-date{color:#595959 !important;}' +
    '.ti-widget .ti-footer,.ti-widget .ti-footer *{cursor:default !important;}' +
    '.ti-widget .ti-footer a,.ti-widget .ti-footer a *{cursor:pointer !important;}' +
    '.a11y-video-toggle{position:absolute;right:16px;bottom:16px;z-index:5;background:rgba(0,0,0,.7);color:#fff;border:2px solid #fff;border-radius:999px;padding:8px 14px;font:600 14px/1 sans-serif;cursor:pointer;}' +
    '.a11y-video-toggle:focus-visible{outline:3px solid #fff;outline-offset:3px;}';

  // 16. HubSpot form (same-origin iframe): placeholder text and Submit button contrast (1.4.3)
  var HS_CSS =
    '.hs-input::placeholder{color:#6b6b6b !important;opacity:1 !important;}' +
    'select.hs-input.is-placeholder{color:#6b6b6b !important;}' +
    'select.hs-input.is-placeholder option{color:#33475b;}' +
    '.hs-button,.hs-button.primary{background-color:#0A6FD6 !important;border-color:#0A6FD6 !important;color:#ffffff !important;}' +
    '.hs-button:hover,.hs-button:focus,.hs-button.primary:hover,.hs-button.primary:focus{background-color:#0858AB !important;border-color:#0858AB !important;}';
  function styleHubspot(frame) {
    try {
      var d = frame.contentDocument;
      if (!d || !d.head || d.querySelector('style[data-a11y-fixes]')) return;
      var s = d.createElement('style');
      s.setAttribute('data-a11y-fixes', '');
      s.appendChild(d.createTextNode(HS_CSS));
      d.head.appendChild(s);
    } catch (e) { /* cross-origin: nothing we can do from here */ }
  }
  function wireHubspot() {
    document.querySelectorAll('iframe[id^="hs-form-iframe"], iframe.hs-form-iframe').forEach(function (f) {
      styleHubspot(f);
      if (!f.dataset.a11yWired) {
        f.dataset.a11yWired = '1';
        f.addEventListener('load', function () { styleHubspot(f); });
      }
    });
  }
  var style = document.createElement('style');
  style.setAttribute('data-a11y-fixes', '');
  style.appendChild(document.createTextNode(css));
  document.head.appendChild(style);

  // 1. Hide slides that are scrolled out of view inside each slider
  function updateSlides(container) {
    var box = container.getBoundingClientRect();
    if (!box.width) return;
    container.querySelectorAll('.swiper-slide').forEach(function (slide) {
      var r = slide.getBoundingClientRect();
      var offscreen = r.right <= box.left + 2 || r.left >= box.right - 2;
      var focusables = slide.querySelectorAll('a[href], button, input, select, textarea, [tabindex]');
      if (offscreen) {
        slide.setAttribute('aria-hidden', 'true');
        slide.inert = true;
        // Also take links out of the tab order so nothing focusable sits inside aria-hidden
        focusables.forEach(function (el) {
          if (!el.hasAttribute('data-a11y-tab')) el.setAttribute('data-a11y-tab', el.getAttribute('tabindex') || '');
          el.setAttribute('tabindex', '-1');
        });
      } else {
        slide.removeAttribute('aria-hidden');
        slide.inert = false;
        focusables.forEach(function (el) {
          if (!el.hasAttribute('data-a11y-tab')) return;
          var orig = el.getAttribute('data-a11y-tab');
          if (orig) el.setAttribute('tabindex', orig); else el.removeAttribute('tabindex');
          el.removeAttribute('data-a11y-tab');
        });
      }
    });
  }

  function wireSliders() {
    document.querySelectorAll('.swiper-initialized').forEach(function (container) {
      if (container.dataset.a11yWired) { updateSlides(container); return; }
      container.dataset.a11yWired = '1';
      var update = function () { updateSlides(container); };
      if (container.swiper && container.swiper.on) {
        container.swiper.on('slideChangeTransitionEnd', update);
        container.swiper.on('resize', update);
      }
      container.addEventListener('transitionend', update);
      update();
    });
  }

  function fixAll() {
    // 2. Slider arrow icons are decorative; the buttons already have labels
    document.querySelectorAll('.swiper-button-prev svg, .swiper-button-next svg').forEach(function (svg) {
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('focusable', 'false');
    });

    // 4. Trustindex "Read more" toggles: ONE keyboard-operable button per toggle.
    //    Markup is <span.ti-read-more><span.ti-read-more-active>Read more</span></span>; only the
    //    outer span is the button, the inner span is plain text (no nested interactive controls).
    document.querySelectorAll('.ti-read-more-active, .ti-read-more').forEach(function (el) {
      var isInner = el.classList.contains('ti-read-more-active') && el.parentElement && el.parentElement.closest('.ti-read-more');
      if (isInner) {
        el.removeAttribute('role');
        el.removeAttribute('tabindex');
        el.removeAttribute('aria-label');
        el.removeAttribute('data-a11y');
        return;
      }
      // Empty placeholders (no "Read more" text, sometimes just &nbsp; and opacity 0) must not be focusable
      // (hidden with display:none via CSS; Trustindex manages their tabindex itself, so leave it alone)
      if (!el.textContent.trim()) {
        el.removeAttribute('role');
        el.removeAttribute('aria-expanded');
        el.removeAttribute('data-a11y');
        return;
      }
      var collapse = el.getAttribute('data-collapse-text');
      if (collapse) el.setAttribute('aria-expanded', el.textContent.trim() === collapse ? 'true' : 'false');
      if (el.hasAttribute('data-a11y')) return;
      el.setAttribute('data-a11y', '1');
      el.setAttribute('role', 'button');
      el.setAttribute('tabindex', '0');
      el.removeAttribute('aria-hidden');
      el.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          var target = el.querySelector('.ti-read-more-active') || el;
          target.click(); // bubbles to Trustindex's handler on either span
        }
      });
    });

    // 6. Trustindex star ratings
    document.querySelectorAll('.ti-stars:not([data-a11y])').forEach(function (stars) {
      var imgs = stars.querySelectorAll('img');
      if (!imgs.length) return;
      stars.setAttribute('data-a11y', '1');
      var full = stars.querySelectorAll('img.f, img[src*="/f.svg"]').length;
      var half = stars.querySelectorAll('img.h, img[src*="/h.svg"]').length;
      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', 'Rated ' + (full + (half ? 0.5 : 0)) + ' out of ' + imgs.length + ' stars');
      imgs.forEach(function (img) { img.setAttribute('alt', ''); });
    });

    // 7. Repeated review-source logos are decorative
    document.querySelectorAll('[class*="ti-review-item"] img[alt="Google"]').forEach(function (img) {
      img.setAttribute('alt', '');
    });

    // 8. Announce links that open a new tab
    document.querySelectorAll('a[target="_blank"]:not([data-a11y-nt])').forEach(function (a) {
      a.setAttribute('data-a11y-nt', '1');
      var note = document.createElement('span');
      note.className = 'visually-hidden';
      note.textContent = ' (opens in a new tab)';
      a.appendChild(note);
    });

    // 9. <b> in page content -> <strong>
    document.querySelectorAll('.shogun-root b').forEach(function (b) {
      var s = document.createElement('strong');
      Array.prototype.forEach.call(b.attributes, function (a) { s.setAttribute(a.name, a.value); });
      s.innerHTML = b.innerHTML;
      b.replaceWith(s);
    });

    // 10. Label headings that come before the H1 become plain text (styles preserved)
    var h1 = document.querySelector('h1');
    if (h1) {
      document.querySelectorAll('#HeaderCountryMobileLabel, #HeaderCountryLabel, .cart-notification__heading').forEach(function (h) {
        if (!/^H[2-6]$/.test(h.tagName)) return;
        if (!(h.compareDocumentPosition(h1) & Node.DOCUMENT_POSITION_FOLLOWING)) return;
        var cs = getComputedStyle(h);
        var keep = {};
        ['fontFamily', 'fontSize', 'fontWeight', 'letterSpacing', 'lineHeight', 'textTransform', 'marginTop', 'marginBottom'].forEach(function (p) { keep[p] = cs[p]; });
        var el = document.createElement(h.classList.contains('visually-hidden') ? 'span' : 'p');
        Array.prototype.forEach.call(h.attributes, function (a) { el.setAttribute(a.name, a.value); });
        if (!h.classList.contains('visually-hidden')) {
          Object.keys(keep).forEach(function (p) {
            el.style.setProperty(p.replace(/[A-Z]/g, function (m) { return '-' + m.toLowerCase(); }), keep[p], 'important');
          });
        }
        el.innerHTML = h.innerHTML;
        h.replaceWith(el);
      });
    }

    // 12. PaigeChat widget: put it inside a named landmark so it isn't "orphaned" content
    var chat = document.getElementById('paigechat-widget');
    if (chat && !chat.hasAttribute('role')) {
      chat.setAttribute('role', 'complementary');
      chat.setAttribute('aria-label', 'Chat with VertiMax');
    }

    // 13. Trustindex rating summary: its "N reviews" link already gives keyboard access, so the
    //     wrapper is NOT made a button (that nested a link inside a button). The pointer cursor on
    //     the non-link parts is removed via CSS so they don't look clickable without being focusable.

    // 17. Decorative images with alt="" (review stars, Google icons, parallax background) are also
    //     hidden from assistive tech so checkers don't flag them as mishandled decorative images (1.1.1)
    document.querySelectorAll('.ti-widget img[alt=""], .jarallax-container img[alt=""], .jarallax-container img:not([alt])').forEach(function (img) {
      img.setAttribute('alt', '');
      img.setAttribute('role', 'presentation');
      img.setAttribute('aria-hidden', 'true');
    });

    // 14. Untitled iframes (PayTomorrow financing widget)
    document.querySelectorAll('iframe#pt-iframe-mpe:not([title]), iframe[src*="paytomorrow"]:not([title])').forEach(function (f) {
      f.setAttribute('title', 'PayTomorrow financing');
    });
    //     HubSpot's default "Form 0" title is not descriptive
    document.querySelectorAll('iframe[id^="hs-form-iframe"]').forEach(function (f) {
      if (!f.title || /^Form \d+$/.test(f.title)) f.setAttribute('title', 'Contact form');
    });
    //     Any other iframe without a title (Shopify pixel sandbox, Facebook SDK, admin preview bar)
    document.querySelectorAll('iframe').forEach(function (f) {
      if (f.title && f.title.trim()) return;
      var id = f.id || '', src = f.getAttribute('src') || '';
      var title = 'Embedded content';
      if (/^web-pixel/.test(id)) title = 'Shopify analytics (no visible content)';
      else if (/^fb/.test(id) || /facebook\.(com|net)/.test(src)) title = 'Facebook (no visible content)';
      else if (id === 'PBarNextFrame') title = 'Shopify preview bar';
      f.setAttribute('title', title);
      // Hidden or 1-4px tracking frames: keep them away from screen readers and the keyboard
      var r = f.getBoundingClientRect();
      if (title.indexOf('no visible content') !== -1 || (r.width <= 4 && r.height <= 4)) {
        f.setAttribute('aria-hidden', 'true');
        f.setAttribute('tabindex', '-1');
      }
    });

    // 15. Consistent link names for the same destination (WCAG 3.2.4)
    //     Accessible names always contain the visible text (WCAG 2.5.3).
    var logo = document.querySelector('.header__heading-link');
    if (logo && !logo.hasAttribute('aria-label')) logo.setAttribute('aria-label', 'VertiMax');
    var cartIcon = document.getElementById('cart-icon-bubble');
    if (cartIcon && !cartIcon.hasAttribute('aria-label')) cartIcon.setAttribute('aria-label', 'View cart');
    var names = { '/pages/v8': 'Explore V-Series Platforms', '/pages/raptor': 'Explore Raptor Systems' };
    document.querySelectorAll('a[href]').forEach(function (a) {
      var name = names[a.pathname.replace(/\/$/, '')];
      if (!name || a.hasAttribute('data-a11y-nm')) return;
      var visible = (a.textContent || '').replace(/\s+/g, ' ').trim();
      if (visible && name.toLowerCase().indexOf(visible.toLowerCase()) === -1) return; // keep label-in-name intact
      a.setAttribute('data-a11y-nm', '1');
      // Product cards: keep the product name available as a description
      var card = a.closest('.bb-card');
      var title = card && card.querySelector('h3, .bb-card__title');
      if (title) {
        if (!title.id) title.id = 'bb-title-' + Math.random().toString(36).slice(2, 8);
        a.setAttribute('aria-describedby', title.id);
      }
      a.setAttribute('aria-label', name);
    });

    // 5. Decorative parallax background images
    document.querySelectorAll('.jarallax-container img:not([alt])').forEach(function (img) {
      img.setAttribute('alt', '');
    });

    // 18. Auto-playing, looping background videos need a way to pause them (WCAG 2.2.2)
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    document.querySelectorAll('video[autoplay]:not([controls]):not([data-a11y-vt])').forEach(function (v) {
      v.setAttribute('data-a11y-vt', '1');
      var box = v.parentElement;
      if (!box) return;
      if (getComputedStyle(box).position === 'static') box.style.position = 'relative';
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'a11y-video-toggle';
      function sync() {
        var paused = v.paused;
        btn.textContent = paused ? 'Play background video' : 'Pause background video';
      }
      btn.addEventListener('click', function () {
        if (v.paused) { v.play(); } else { v.pause(); }
      });
      v.addEventListener('play', sync);
      v.addEventListener('pause', sync);
      box.appendChild(btn);
      if (reduce) { v.removeAttribute('autoplay'); v.pause(); }
      sync();
    });

    // 19. Facebook pixel briefly adds a hidden form of unlabeled data fields; keep it out of the
    //     accessibility tree and label the fields so checkers don't report them
    document.querySelectorAll('form').forEach(function (f) {
      if (f.hasAttribute('data-a11y-fb')) return;
      if (!f.querySelector('input[name="ev"]') || !f.querySelector('input[name="id"]')) return;
      f.setAttribute('data-a11y-fb', '1');
      f.setAttribute('aria-hidden', 'true');
      f.querySelectorAll('input, select, textarea').forEach(function (i) {
        if (!i.hasAttribute('aria-label')) i.setAttribute('aria-label', 'Facebook pixel data');
        i.setAttribute('tabindex', '-1');
      });
    });

    wireSliders();
    wireHubspot();
  }

  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    setTimeout(function () { queued = false; fixAll(); }, 250);
  }

  function start() {
    fixAll();
    new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
  window.addEventListener('load', fixAll);
  window.addEventListener('resize', schedule);
})();